import { Injectable, inject } from '@angular/core';

import { ExtendedTab } from '@hdms/shared/common-types';

import { EnvironmentService } from '../environment';

export interface CustomLinkExtendedTab extends Omit<ExtendedTab, 'url'> {
  url?: string | ((customLinkService: CustomLinksService) => string);
}

/**
 * CustomLinksService provides access to custom links, which should be added to user account menu.
 * Custom links will be configured via deployment properties and applied via node process env.
 * See README.md for further info.
 */

@Injectable({
  providedIn: 'root'
})
export class CustomLinksService {
  private readonly envService = inject(EnvironmentService);

  /**
   * Replacement map containing keys to be replaced with some environment properties.
   * The map can be extended with additional replacement keys.
   * @private
   */
  private readonly replacementMap = {
    '{{languageTagLong}}': this.envService.languageTagLong
  };

  private readonly replacementKeys = Object.keys(this.replacementMap);
  private readonly customLinks = this.envService.customLinks;

  public get(key: string): string {
    const link = this.customLinks[key];
    return link ? this.updateLink(link) : null;
  }

  public parseUrl(url: CustomLinkExtendedTab['url']): string {
    return typeof url === 'string' ? url : url(this);
  }

  private updateLink(url: string): string {
    return this.replacementKeys.reduce((updatedUrl, key) => updatedUrl.replace(key, this.replacementMap[key]), url);
  }
}
